// frontend/src/features/ups/UpsDashboard.js
// UPS Admin dashboard layout:
// - Collapsible sidebar navigation
// - Global search & filters (scoped to driver views)
// - Stats + GlobalResultsDrawer shown only inside the drivers section
//
// Option A (canonical index URL):
// - Active tab is the index route: /ups-admin/drivers
// - /ups-admin/drivers/active may exist as an alias, but we treat /drivers as canonical.

import React, { useState, useEffect, useMemo, useRef } from 'react';
import { NavLink, Outlet, useNavigate, useMatch, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  LayoutList,
  Ban as BannedIcon,
  Users,
  Building2,
  Menu,
  X,
  UserCheck,
  AlertTriangle,
  Inbox,
  MapPin,
  Truck,
  ArrowUp,
} from 'lucide-react';

import UpsAccountMenu from './UpsAccountMenu';
import UpsDriverStats from './UpsDriverStats';

// Phase 7: site selector load when entering drivers section
import {
  loadMyCompanySites,
  selectCompanySites,
  selectSitesLoading,
} from '../sites/sitesSlice';

// Global filters (used by GlobalResultsDrawer + driver list tabs)
import { selectAgencyId, selectFilters, setScope, setQuery } from '../ui/filtersSlice';

// Global search drawer (results open in correct tab)
import GlobalResultsDrawer from '../../components/GlobalResultsDrawer';
import CompanyThemeProvider from '../../theme/CompanyThemeProvider';

const upsRouteTitles = [
  { path: '/ups-admin/drivers', title: 'Active Drivers' },
  { path: '/ups-admin/drivers/reserved', title: 'Reserved Drivers' },
  { path: '/ups-admin/drivers/requests', title: 'Requests' },
  { path: '/ups-admin/drivers/banned', title: 'Banned Drivers' },
  { path: '/ups-admin/drivers/blocked', title: 'Blocked / Orphaned' },
  { path: '/ups-admin/manage-sites', title: 'Manage Sites' },
  { path: '/ups-admin/manage-internal-drivers', title: 'Manage Internal Drivers' },
  { path: '/ups-admin/manage-agencies', title: 'Manage Agencies' },
  { path: '/ups-admin/accounts', title: 'Manage Accounts' },
];

// Maps canonical GlobalResultsDrawer route keys to the correct tab route.
const routeKeyToRoute = {
  active: '/ups-admin/drivers',
  requests: '/ups-admin/drivers/requests',
  reserved: '/ups-admin/drivers/reserved',
  banned: '/ups-admin/drivers/banned',
};

// Sidebar presentation helpers:
const btnBase = (collapsed) => 
  `group flex items-center font-semibold ${
    collapsed 
      ? 'h-11 w-11 justify-center rounded-xl px-0 mx-auto' 
      : 'w-full rounded-xl px-3 py-2.5 text-sm'
  }`;

const navItemClass = ({ isActive }) =>
  isActive
    ? 'bg-app-primary text-white shadow-sm'
    : 'text-app-text hover:bg-app-sidebarActive hover:text-app-primary';

// Icon is controlled by parent layout now, no need for mx-auto
const navIconClass = 'h-5 w-5 shrink-0';

const sectionLabelClass =
  'px-3 pt-5 pb-2 text-[11px] font-bold uppercase tracking-[0.22em] text-app-muted';

export default function UpsDashboard() {
  const [collapsed, setCollapsed] = useState(false);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [showScrollTop, setShowScrollTop] = useState(false);
  const mainScrollRef = useRef(null);

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const pageTitle = useMemo(() => {
    const pathname = location.pathname.replace(/\/+$/, '');

    const exact = upsRouteTitles.find((r) => r.path === pathname);
    if (exact) return exact.title;

    const match = [...upsRouteTitles]
      .sort((a, b) => b.path.length - a.path.length)
      .find((r) => pathname.startsWith(r.path));

    return match?.title || 'Driver Management';
  }, [location.pathname]);

  const agencyId = useSelector(selectAgencyId);
  const { q } = useSelector(selectFilters);
  const companySites = useSelector(selectCompanySites);
  const sitesLoading = useSelector(selectSitesLoading);

  // We only show driver stats + global drawer when user is in the drivers section.
  const matchDriversRoot = useMatch('/ups-admin/drivers');
  const matchDriversAny = useMatch('/ups-admin/drivers/*');
  const inDriversSection = Boolean(matchDriversRoot || matchDriversAny);

  // Load company sites when entering drivers section (SiteSelector depends on it)
  useEffect(() => {
    if (!inDriversSection) return;
    if (companySites.length > 0 || sitesLoading) return;
    dispatch(loadMyCompanySites());
  }, [companySites.length, dispatch, inDriversSection, sitesLoading]);

  // Clear global search + scope when leaving the drivers section
  useEffect(() => {
    if (!inDriversSection) {
      dispatch(setScope('tab'));
      dispatch(setQuery(''));
    }
  }, [inDriversSection, dispatch]);

  useEffect(() => {
    setMobileNavOpen(false);
  }, [location.pathname]);

  const openResultInTab = (row) => {
    const route = routeKeyToRoute[row.routeKey] || '/ups-admin/drivers';
    dispatch(setScope('tab'));

    const params = new URLSearchParams();
    if (agencyId) params.set('agencyId', agencyId);
    if (q) params.set('q', q);

    // Optional focus behavior (if list components support it)
    params.set('focus', String(row.id));

    navigate(`${route}?${params.toString()}`);
  };

  const viewAllForStatus = (routeKey) => {
    const route = routeKeyToRoute[routeKey] || '/ups-admin/drivers';
    dispatch(setScope('tab'));

    const params = new URLSearchParams();
    if (agencyId) params.set('agencyId', agencyId);
    if (q) params.set('q', q);

    navigate(`${route}?${params.toString()}`);
  };

  const handleMainScroll = () => {
    const scrollTop = mainScrollRef.current?.scrollTop || 0;
    setShowScrollTop(scrollTop > 400);
  };

  const scrollMainToTop = () => {
    mainScrollRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const renderSidebarHeader = (isCollapsed, isMobile = false) => (
    <div className="flex h-[var(--ups-shell-header-h)] items-center border-b border-app-border px-4">
      <div className={`flex w-full ${isCollapsed ? 'items-center justify-center gap-2' : 'items-center justify-between gap-3'}`}>
        {isCollapsed ? (
          <button
            onClick={() => setCollapsed(false)}
            className="mx-auto rounded-xl p-2 text-app-muted transition hover:bg-app-sidebarActive hover:text-app-primary"
            aria-label="Expand sidebar"
          >
            <Menu size={22} />
          </button>
        ) : (
          <>
            <div className="flex items-center gap-3">
              <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-app-primary text-sm font-extrabold text-app-accent shadow-sm">
                UPS
              </div>
              <div>
                <p className="text-[11px] font-bold uppercase tracking-[0.24em] text-app-muted">
                  Platform
                </p>
                <h2 className="text-base font-extrabold text-app-text">
                  Control Center
                </h2>
              </div>
            </div>
            <button
              onClick={() => (isMobile ? setMobileNavOpen(false) : setCollapsed(true))}
              className="rounded-xl p-2 text-app-muted transition hover:bg-app-sidebarActive hover:text-app-primary"
              aria-label={isMobile ? 'Close navigation' : 'Collapse sidebar'}
            >
              <X size={20} />
            </button>
          </>
        )}
      </div>
    </div>
  );

  const renderSidebarNav = (isCollapsed, onNavigate) => (
    <nav className="flex-1 space-y-2 px-3 py-4">
      {!isCollapsed && <div className={sectionLabelClass}>Operations</div>}

      <NavLink
        to="/ups-admin/drivers"
        end
        onClick={onNavigate}
        className={({ isActive }) => `${btnBase(isCollapsed)} ${navItemClass({ isActive })}`}
        title="Active Drivers"
      >
        <LayoutList className={navIconClass} />
        {!isCollapsed && <span className="ml-3">Active Drivers</span>}
      </NavLink>

      <NavLink
        to="/ups-admin/drivers/reserved"
        onClick={onNavigate}
        className={({ isActive }) => `${btnBase(isCollapsed)} ${navItemClass({ isActive })}`}
        title="Reserved Drivers"
      >
        <UserCheck className={navIconClass} />
        {!isCollapsed && <span className="ml-3">Reserved Drivers</span>}
      </NavLink>

      <NavLink
        to="/ups-admin/drivers/requests"
        onClick={onNavigate}
        className={({ isActive }) => `${btnBase(isCollapsed)} ${navItemClass({ isActive })}`}
        title="Requests"
      >
        <Inbox className={navIconClass} />
        {!isCollapsed && <span className="ml-3">Requests</span>}
      </NavLink>

      <NavLink
        to="/ups-admin/drivers/banned"
        onClick={onNavigate}
        className={({ isActive }) => `${btnBase(isCollapsed)} ${navItemClass({ isActive })}`}
        title="Banned Drivers"
      >
        <BannedIcon className={navIconClass} />
        {!isCollapsed && <span className="ml-3">Banned Drivers</span>}
      </NavLink>

      <NavLink
        to="/ups-admin/drivers/blocked"
        onClick={onNavigate}
        className={({ isActive }) => `${btnBase(isCollapsed)} ${navItemClass({ isActive })}`}
        title="Blocked / Orphaned"
      >
        <AlertTriangle className={navIconClass} />
        {!isCollapsed && <span className="ml-3">Blocked / Orphaned</span>}
      </NavLink>

      {!isCollapsed ? (
        <div className="mt-5 border-t border-app-border pt-3">
          <div className={sectionLabelClass}>Administration</div>
        </div>
      ) : (
        <div className="mx-auto my-4 w-8 border-t border-app-border"></div>
      )}

      <NavLink
        to="/ups-admin/manage-sites"
        onClick={onNavigate}
        className={({ isActive }) => `${btnBase(isCollapsed)} ${navItemClass({ isActive })}`}
        title="Manage Sites"
      >
        <MapPin className={navIconClass} />
        {!isCollapsed && <span className="ml-3">Manage Sites</span>}
      </NavLink>

      <NavLink
        to="/ups-admin/manage-internal-drivers"
        onClick={onNavigate}
        className={({ isActive }) => `${btnBase(isCollapsed)} ${navItemClass({ isActive })}`}
        title="Manage Internal Drivers"
      >
        <Truck className={navIconClass} />
        {!isCollapsed && <span className="ml-3">Manage Internal Drivers</span>}
      </NavLink>

      <NavLink
        to="/ups-admin/manage-agencies"
        onClick={onNavigate}
        className={({ isActive }) => `${btnBase(isCollapsed)} ${navItemClass({ isActive })}`}
        title="Manage Agencies"
      >
        <Building2 className={navIconClass} />
        {!isCollapsed && <span className="ml-3">Manage Agencies</span>}
      </NavLink>

      <NavLink
        to="/ups-admin/accounts"
        onClick={onNavigate}
        className={({ isActive }) => `${btnBase(isCollapsed)} ${navItemClass({ isActive })}`}
        title="Manage Accounts"
      >
        <Users className={navIconClass} />
        {!isCollapsed && <span className="ml-3">Manage Accounts</span>}
      </NavLink>
    </nav>
  );

  return (
    <CompanyThemeProvider themeKey="ups">
      <div
        className="flex h-[100dvh] min-h-0 overflow-hidden bg-app-canvas"
        style={{ '--ups-shell-header-h': '72px' }}
      >
        {/* Desktop sidebar */}
        <aside
          className={`${
            collapsed ? 'w-[68px]' : 'w-72'
          } hidden shrink-0 flex-col overflow-y-auto border-r border-app-border bg-app-sidebar pb-[env(safe-area-inset-bottom)] shadow-sm lg:flex`}
        >
          {renderSidebarHeader(collapsed)}
          {renderSidebarNav(collapsed)}
        </aside>

        {mobileNavOpen && (
          <div className="fixed inset-0 z-50 lg:hidden" role="dialog" aria-modal="true">
            <button
              type="button"
              className="absolute inset-0 bg-black/40"
              aria-label="Close navigation"
              onClick={() => setMobileNavOpen(false)}
            />
            <aside className="relative flex h-full w-[min(20rem,calc(100vw-3rem))] flex-col overflow-y-auto border-r border-app-border bg-app-sidebar pb-[env(safe-area-inset-bottom)] shadow-xl">
              {renderSidebarHeader(false, true)}
              {renderSidebarNav(false, () => setMobileNavOpen(false))}
            </aside>
          </div>
        )}

        {/* Main content */}
        <main
          ref={mainScrollRef}
          onScroll={handleMainScroll}
          className="relative min-w-0 flex-1 overflow-y-auto bg-app-canvas"
        >
          <header className="flex h-[var(--ups-shell-header-h)] items-center border-b border-app-border bg-app-primary px-4 shadow-sm sm:px-6">
            {/* Replaced fixed width container with full-width flex container */}
            <div className="flex w-full items-center justify-between gap-4">
              <div className="flex min-w-0 items-center gap-3">
                <button
                  type="button"
                  onClick={() => setMobileNavOpen(true)}
                  className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl text-white transition hover:bg-white/10 focus:outline-none focus:ring-2 focus:ring-app-accent/40 lg:hidden"
                  aria-label="Open navigation"
                  aria-expanded={mobileNavOpen}
                >
                  <Menu size={22} />
                </button>
                <div className="min-w-0">
                  <p className="text-xs font-semibold uppercase tracking-[0.28em] text-app-accent">
                    UPS Control Center
                  </p>
                  <h1 className="mt-1 truncate text-lg font-bold text-white sm:text-xl">
                    {pageTitle}
                  </h1>
                </div>
              </div>

              <UpsAccountMenu />
            </div>
          </header>

          <div className="mx-auto w-full max-w-[1600px] px-4 py-6 sm:px-6 sm:py-7">
            {inDriversSection && <UpsDriverStats />}
            {inDriversSection && (
              <GlobalResultsDrawer onOpen={openResultInTab} onViewAll={viewAllForStatus} />
            )}
            <Outlet />
          </div>

          <button
            type="button"
            onClick={scrollMainToTop}
            aria-label="Scroll dashboard content to top"
            className={`fixed bottom-5 right-5 z-40 flex h-11 w-11 items-center justify-center rounded-full border border-app-border bg-app-card text-app-text shadow-md transition hover:bg-app-surface focus:outline-none focus:ring-2 focus:ring-app-primary/30 lg:hidden ${
              showScrollTop
                ? 'translate-y-0 opacity-100'
                : 'pointer-events-none translate-y-2 opacity-0'
            }`}
          >
            <ArrowUp className="h-5 w-5" aria-hidden="true" />
          </button>
        </main>
      </div>
    </CompanyThemeProvider>
  );
}
